import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { BUTTON_RESET, PRESS_CLASS, ROW_CLASS } from "./press";
import { scaledFont } from "@/styles/scaledFont";

export interface RowProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "title"> {
  /** The row's copy — always `t('…')`, never a literal. */
  title: string;
  /** The second line, when the row needs one. */
  sub?: string;
  /** A value or badge, between the text and the chevron. */
  right?: ReactNode;
  /** Drops the hairline: the last row of a group has none. @default false */
  last?: boolean;
  onClick: () => void;
}

const BASE: CSSProperties = {
  ...BUTTON_RESET,
  display: "flex",
  alignItems: "center",
  gap: "var(--space-3)",
  padding: "var(--space-4) var(--space-1)",
  width: "100%",
};

const TITLE: CSSProperties = { font: "var(--t-ui)", color: "var(--d-fg)" };

/* `500 13px/19px` — one pixel of leading off `--t-caption` (500 13px/18px),
 * which is a different token used for a different thing. The handoff's value
 * wins. */
const SUB: CSSProperties = {
  font: scaledFont("500 13px/19px var(--font-body)"),
  color: "var(--d-fg-2)",
  textWrap: "pretty",
};

const CHEVRON: CSSProperties = {
  font: "400 18px var(--font-body)",
  color: "var(--d-fg-3)",
};

/**
 * A line in a list — a saved reading, a setting, a partner. Title, an optional
 * sub, an optional right slot, and the chevron that says it opens something.
 *
 * Ported from `Row` in
 * `docs/design/handoff/design/templates/you/you-screen.jsx`. The prototype's
 * `<div onClick>` is a `<button>` here, and it carries `PRESS_CLASS` on top of
 * the prototype's hover class: 05-SHARED.md's rule is that everything tappable
 * presses, and on a phone the hover never fires.
 */
export function Row({
  title,
  sub,
  right,
  last = false,
  onClick,
  className,
  style,
  ...rest
}: RowProps): JSX.Element {
  return (
    <button
      type="button"
      className={
        className
          ? `${ROW_CLASS} ${PRESS_CLASS} ${className}`
          : `${ROW_CLASS} ${PRESS_CLASS}`
      }
      onClick={onClick}
      style={{
        ...BASE,
        /* Always written, never omitted: React warns when a longhand
         * disappears from an element that also carries the `border`
         * shorthand, and `last` flips between two rows of the same list. */
        borderBottom: last ? "none" : "1px solid var(--d-line)",
        ...style,
      }}
      {...rest}
    >
      <span
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          textAlign: "left",
        }}
      >
        <span style={TITLE}>{title}</span>
        {sub ? (
          <span data-row-sub style={SUB}>
            {sub}
          </span>
        ) : null}
      </span>
      {/* Only when there is one: an always-rendered wrapper is an empty flex
       * item, and the row's 12px gap would be paid twice before the chevron.
       * The prototype writes the slot bare (`{right}`), which renders nothing
       * at all when it is undefined. */}
      {right !== undefined && right !== null ? <span>{right}</span> : null}
      <span data-row-chevron aria-hidden="true" style={CHEVRON}>
        ›
      </span>
    </button>
  );
}

export default Row;
